// src/api/sync.js
// Fire-and-forget cloud sync. localStorage is always the source of truth.
// If any call fails, nothing breaks — the UI never waits on these.

export const BASE = window.Capacitor?.isNativePlatform?.() ? 'https://habitrak.io/api' : '/api';

let _getToken = null;
export function setTokenGetter(fn) { _getToken = fn; }

async function authHeaders(forceRefresh = false) {
  const headers = { 'Content-Type': 'application/json' };
  if (_getToken) {
    const token = await _getToken(forceRefresh);
    if (!token) throw new Error('Auth token unavailable');
    // Azure Static Web Apps reserves the `Authorization` header for its own
    // service-to-service auth to linked (managed) Function Apps and silently
    // overwrites it before our function ever sees it — see api/shared/auth.js.
    // Our own bearer token has to travel in a custom header instead.
    headers['X-Firebase-Auth'] = token;
  }
  return headers;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Every authenticated call goes through here so a stale/expired Firebase ID
// token never surfaces as a hard failure: on a 401 we retry once with a
// forcibly-refreshed token before giving up. This matters most for
// multi-user (non single-user-mode) accounts, where every request is
// actually verified server-side instead of bypassed.
async function authFetch(path, options = {}, timeoutMs = 15000) {
  let res = await fetchWithTimeout(`${BASE}${path}`, {
    ...options,
    headers: await authHeaders(false),
  }, timeoutMs);

  if (res.status === 401) {
    res = await fetchWithTimeout(`${BASE}${path}`, {
      ...options,
      headers: await authHeaders(true),
    }, timeoutMs);
  }

  return res;
}

async function fetchJson(path, timeoutMs = 15000) {
  const res = await authFetch(path, {}, timeoutMs);
  if (!res.ok) throw new Error(`GET ${path} failed (${res.status})`);
  return res.json();
}

async function post(path, body) {
  try {
    await authFetch(path, { method: 'POST', body: JSON.stringify(body) });
  } catch { /* intentional: fire and forget */ }
}

async function del(path) {
  try {
    await authFetch(path, { method: 'DELETE' });
  } catch { /* intentional: fire and forget */ }
}

async function postAwait(path, body) {
  const res = await authFetch(path, { method: 'POST', body: JSON.stringify(body) }, 15000);
  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      // `detail` (when present) carries the real server-side error — see the
      // TEMP diagnostic in api/stash/index.js — `error` alone is often just a
      // generic label like "Database error".
      detail = data?.detail
        ? `: ${data.error ? `${data.error} — ` : ''}${data.detail}`
        : data?.error
          ? `: ${data.error}`
          : '';
    } catch {
      // Fallback to status text when response is not JSON.
      detail = res.statusText ? `: ${res.statusText}` : '';
    }
    throw new Error(`POST ${path} failed (${res.status})${detail}`);
  }
}

// Called once on login to create the DB user record if it doesn't exist yet.
export async function ensureUser() {
  const ref = localStorage.getItem('ht_ref');
  const res = await authFetch('/users/me', {
    method: 'POST',
    body: JSON.stringify({ referralCode: ref || null }),
  }, 15000);

  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      detail = data?.detail
        ? `: ${data.detail}`
        : data?.error
          ? `: ${data.error}`
          : '';
    } catch {
      detail = res.statusText ? `: ${res.statusText}` : '';
    }
    throw new Error(`/users/me failed (${res.status})${detail}`);
  }

  return true;
}

// Scan retail packaging photos and extract stash data. Images are sent as
// base64 and never written anywhere by the caller — see api/package-scan.
export async function scanPackage(images, category) {
  const res = await authFetch('/package-scan', {
    method: 'POST',
    body: JSON.stringify({ images, category: category || null }),
  }, 30000);

  let data = null;
  try { data = await res.json(); } catch { /* non-JSON error body */ }

  if (!res.ok) throw new Error(data?.error || `Scan failed (${res.status})`);
  return data;
}

// Map localStorage intake entry → API body. Shared by syncIntake() (live,
// fire-and-forget) and uploadLocalCache() (bulk, awaited) so this mapping
// only has to be correct in one place.
function toIntakeBody(entry) {
  return {
    method:       entry.method,
    stashId:      entry.stashId      || null,
    stashName:    entry.stashName    || null,
    stashCategory:entry.stashCat     || null,
    weight:       entry.weight       || null,
    quantity:     entry.qty          || 1,
    // Quick Intake stores the oil-vape hit count as `oilHits`, the detailed
    // form stores it as `hits` — the app's own display code already checks
    // both (App.jsx: `entry.hits || entry.oilHits`); this was only checking
    // one, so Quick Intake's hit count silently synced as null.
    oilVapeHits:  entry.hits || entry.oilHits || null,
    vapeType:     entry.vapeType     || null,
    edibleSize:   entry.edibleSize   || null,
    edibleQty:    entry.edibleQty    || null,
    tinctureML:   entry.tinctureML   || null,
    thcRaw:       entry.thcRaw       || null,
    thcLo:        entry.thcLo        || null,
    thcHi:        entry.thcHi        || null,
    additives:    entry.additives    || [],
    jointSharing: entry.jointSharing || false,
    jointHits:    entry.jointHits    || null,
    jointSize:    entry.jointSize    || null,
    activity:     entry.activities   || [],
    feelings:     entry.feelings     || [],
    notes:        entry.note         || null,
    ts:           entry.ts,
  };
}

export function syncIntake(entry) {
  post('/intake', toIntakeBody(entry));
}

// Map localStorage session entry → API body
export function syncSession(entry) {
  post('/sessions', {
    medicated: entry.used ?? true,
    ts: entry.ts || null,
  });
}

// Map localStorage checkin entry → API body
export function syncCheckin(entry) {
  post('/checkins', {
    feelings:   entry.feelings   || [],
    activities: entry.activities || [],
    notes:      entry.note       || null,
    ts:         entry.ts         || null,
  });
}

// ─── Read path: pull DB data and map to localStorage format ────────────────

// Returns null only on a total, unexpected failure. On a partial failure (some
// endpoints unreachable — a stale/unrefreshable token, a network blip, one
// route down) it still returns whatever succeeded, plus `errors` naming what
// didn't — so a real fetch failure never gets silently reported as "already
// up to date" / an empty cloud, which look identical to a caller that just
// checks array length.
export async function pullData() {
  const errors = [];
  const fetchSafe = async (path) => {
    try { return await fetchJson(path); }
    catch (e) { errors.push(`${path} (${e.message})`); return null; }
  };

  try {
    const [stashRes, intakeRes, sessionsRes, checkinsRes] = await Promise.all([
      fetchSafe('/stash'),
      fetchSafe('/intake?days=365'),
      fetchSafe('/sessions?days=365'),
      fetchSafe('/checkins?days=365'),
    ]);
    return {
      stash:    (stashRes?.stash       || []).map(dbStashToLocal),
      intake:   (intakeRes?.intake     || []).map(dbIntakeToLocal),
      sessions: (sessionsRes?.sessions || []).map(dbSessionToLocal),
      checkins: (checkinsRes?.checkins || []).map(dbCheckinToLocal),
      errors: errors.length ? errors : null,
    };
  } catch {
    return null;
  }
}

const DB_NAME_TO_STRENGTH = { THC: 'THC', CBD: 'CBD', THCV: 'THCv', CBGA: 'CBGa', CBN: 'CBDn', CBC: 'CBC' };

function dbStashToLocal(s) {
  const cann  = s.cannabinoids || [];
  const getPct = name => cann.find(c => c.name === name)?.value_pct ?? '';
  // Mirrors strengthCannabinoids() in toStashBody() above — same field/unit
  // convention, reversed. mg takes priority since a row is only ever mg OR
  // pct for a given item (never both), but fall back to pct just in case.
  const strength = {};
  for (const row of cann) {
    const field = DB_NAME_TO_STRENGTH[row.name];
    if (!field) continue;
    const val = row.value_mg ?? row.value_pct;
    if (val != null) strength[field] = String(val);
  }
  return {
    id:           s.id,
    clientItemId: s.client_item_id || '',
    cat:          s.category,
    name:         s.name,
    manufacturer: s.brand         || '',
    retailUrl:    s.retail_url    || '',
    weight:       s.weight_grams  != null ? String(s.weight_grams)  : '',
    totalPrice:   s.price         != null ? String(s.price)         : '',
    rating:       s.rating        || 0,
    comment:      s.notes         || '',
    favorite:     s.is_favorite   || false,
    isCurrent:    s.is_current    || false,
    finished:     s.is_finished   || false,
    growType:     s.grow_type     || 'Unknown',
    liveResin:    s.live_resin    || false,
    liveRosin:    s.live_rosin    || false,
    potency:      s.total_thc_pct != null ? String(s.total_thc_pct) : '',
    thca:         getPct('THCA'),
    d9thc:        getPct('D9 THC'),
    cbd:          getPct('CBD'),
    strength:     Object.keys(strength).length ? strength : null, // null (not {}) so `s.strength || emptyStrength()` in App.jsx falls back correctly
    terpenes:     (s.terpenes || []).map(t => ({ name: t.name, pct: t.pct ?? '' })),
    reupAt:       s.reup_at || null,
    // Package-photo-derived fields (see api/package-scan)
    producerName:      s.manufacturer   || '',
    licenseNumber:     s.license_number || '',
    batchId:           s.batch_id       || '',
    flowerType:        s.flower_type    || '',
    solventType:       s.solvent_type   || '',
    vegan:             s.vegan          ?? null,
    kosher:            s.kosher         ?? null,
    glutenFree:        s.gluten_free    ?? null,
    nonGmo:            s.non_gmo        ?? null,
    ingredients:       s.ingredients        || [],
    flavorAdditives:   s.flavor_additives   || [],
    infusionDetails:   s.infusion_details   || '',
    nutrition:         s.nutrition          || null,
    expirationDate:    s.expiration_date    || '',
    packagedDate:      s.packaged_date      || '',
    totalCannabinoidPct: s.total_cannabinoids_pct != null ? String(s.total_cannabinoids_pct) : '',
    totalTerpenePct:     s.total_terpenes_pct     != null ? String(s.total_terpenes_pct)     : '',
    scanSource:        s.scan_source        || null,
  };
}

function dbIntakeToLocal(i) {
  return {
    id:          new Date(i.logged_at).getTime(),
    ts:          i.logged_at,
    method:      i.method,
    stashId:     i.stash_id   || null,
    stashName:   i.stash_name || '',
    stashCat:    i.stash_category || '',
    weight:      i.weight_grams   ?? '',
    qty:         i.quantity       || 1,
    hits:        i.hits ?? i.oil_vape_hits ?? '',
    vapeType:    i.vape_type      || '',
    edibleSize:  i.edible_size_mg ?? '',
    edibleQty:   i.edible_qty     ?? '',
    tinctureML:  i.tincture_ml    ?? '',
    thcRaw:      i.thc_raw_mg     ?? '',
    thcLo:       i.thc_absorbed_lo ?? '',
    thcHi:       i.thc_absorbed_hi ?? '',
    additives:   i.additives      || [],
    jointSharing:i.joint_sharing  || false,
    jointHits:   i.joint_hits     ?? '',
    jointSize:   i.joint_size     ?? '',
    activities:  i.activity       || [],
    feelings:    i.feelings       || [],
    note:        i.notes          || '',
  };
}

function dbSessionToLocal(s) {
  return {
    id:   new Date(s.logged_at).getTime(),
    ts:   s.logged_at,
    used: s.medicated,
    date: s.session_date,
  };
}

function dbCheckinToLocal(c) {
  return {
    id:         new Date(c.logged_at).getTime(),
    ts:         c.logged_at,
    feelings:   c.feelings   || [],
    activities: c.activities || [],
    note:       c.notes      || '',
  };
}

// ───────────────────────────────────────────────────────────────────────────

// Map localStorage stash item → API body
export function syncStash(item) {
  post('/stash', toStashBody(item));
}

// Delete a stash item from the cloud. `item.id` is a local Date.now()
// timestamp for anything created on-device — syncStash/post() is
// fire-and-forget and never hands back the server's UUID for that row — so
// send clientItemId (stable, known to the server too) when we have one, and
// fall back to id for items that came from a cloud pull (whose local id IS
// the server UUID). The API matches either.
export function deleteStash(item) {
  const identifier = item?.clientItemId || item?.id;
  if (!identifier) return;
  del(`/stash/${encodeURIComponent(identifier)}`);
}

function stashSignature(item) {
  if (item?.clientItemId) {
    return `client::${String(item.clientItemId).trim().toLowerCase()}`;
  }
  const parts = [
    item?.cat,
    item?.name,
    item?.manufacturer,
    item?.batchId,
    item?.reupAt,
    item?.packagedDate,
    item?.weight,
    item?.totalPrice,
  ];
  return parts.map(v => String(v || '').trim().toLowerCase()).join('::');
}

// item.strength (THC/CBD/THCv/CBGa/CBDn/CBC — see CANNAB_FIELDS in App.jsx) is
// populated for Edible & Tincture (mg per piece/ml) and Vape Cart (%), but
// was never included in what gets synced to the cloud — local-only data that
// silently vanished on a fresh pull/device. THC/CBD map to the same DB name
// as the dedicated potency/cbd fields above, so skip those two here whenever
// the dedicated field already covered them (only actually overlaps for Vape
// Cart, which has both) to avoid pushing two rows for the same cannabinoid.
const STRENGTH_TO_DB_NAME = { THC: 'THC', CBD: 'CBD', THCv: 'THCV', CBGa: 'CBGA', CBDn: 'CBN', CBC: 'CBC' };

function strengthCannabinoids(item) {
  if (!item.strength || typeof item.strength !== 'object') return [];
  const unitKey = item.cat === 'Vape Cart' ? 'value_pct' : 'value_mg';
  const out = [];
  for (const [field, dbName] of Object.entries(STRENGTH_TO_DB_NAME)) {
    if (field === 'THC' && item.potency) continue;
    if (field === 'CBD' && item.cbd) continue;
    const val = parseFloat(item.strength[field]);
    if (!Number.isFinite(val)) continue;
    out.push({ name: dbName, [unitKey]: val });
  }
  return out;
}

function toStashBody(item) {
  const cannabinoids = [];
  if (item.thca)  cannabinoids.push({ name: 'THCA',   value_pct: parseFloat(item.thca) });
  if (item.d9thc) cannabinoids.push({ name: 'D9 THC', value_pct: parseFloat(item.d9thc) });
  if (item.potency && !item.thca && !item.d9thc)
                  cannabinoids.push({ name: 'THC',    value_pct: parseFloat(item.potency) });
  if (item.cbd)   cannabinoids.push({ name: 'CBD',    value_pct: parseFloat(item.cbd) });
  cannabinoids.push(...strengthCannabinoids(item));

  const terpenes = (item.terpenes || []).map(t =>
    typeof t === 'string' ? { name: t, pct: null } : { name: t.name, pct: t.pct || null }
  );

  return {
    clientItemId: item.clientItemId || null,
    category:    item.cat,
    name:        item.name,
    brand:       item.manufacturer  || null,
    retailUrl:   item.retailUrl     || null,
    weightGrams: item.weight        ? parseFloat(item.weight) : null,
    price:       item.totalPrice    ? parseFloat(item.totalPrice) : null,
    rating:      item.rating        || null,
    notes:       item.comment       || null,
    isFavorite:  item.favorite      || false,
    isCurrent:   item.isCurrent     || false,
    isFinished:  item.finished      || false,
    reupAt:      item.reupAt        || null,
    growType:    item.growType      || 'Unknown',
    liveResin:   item.liveResin     || false,
    liveRosin:   item.liveRosin     || false,
    strainName:  item.name          || null,
    cannabinoids,
    terpenes,
    // Package-photo-derived fields (see api/package-scan) — pass through when present
    manufacturer:          item.producerName    || null,
    licenseNumber:          item.licenseNumber    || null,
    batchId:                item.batchId          || null,
    flowerType:             item.flowerType       || null,
    solventType:            item.solventType      || null,
    vegan:                  item.vegan            ?? null,
    kosher:                 item.kosher           ?? null,
    glutenFree:             item.glutenFree       ?? null,
    nonGmo:                 item.nonGmo           ?? null,
    ingredients:            item.ingredients      || [],
    flavorAdditives:        item.flavorAdditives  || [],
    infusionDetails:        item.infusionDetails  || null,
    nutrition:              item.nutrition        || null,
    expirationDate:         item.expirationDate   || null,
    packagedDate:           item.packagedDate     || null,
    totalCannabinoidsPct:   item.totalCannabinoidPct ? parseFloat(item.totalCannabinoidPct) : null,
    totalTerpenesPct:       item.totalTerpenePct     ? parseFloat(item.totalTerpenePct)     : null,
    scanSource:             item.scanSource       || null,
  };
}

export async function uploadLocalCache(localData) {
  const userReady = await ensureUser();
  if (!userReady) throw new Error('Could not initialize your cloud user profile.');

  const cloud = await pullData();
  if (!cloud) throw new Error('Cloud data unavailable');
  // Uploading against an incomplete picture of the cloud risks creating
  // duplicates (dedup below only sees what actually came back) — safer to
  // stop and let the user retry than silently double up their data.
  if (cloud.errors) throw new Error(`Cloud data incomplete — couldn't reach: ${cloud.errors.join(', ')}`);

  const summary = {
    uploaded: { stash: 0, intake: 0, sessions: 0, checkins: 0 },
    skipped:  { stash: 0, intake: 0, sessions: 0, checkins: 0 },
  };

  const localStash = Array.isArray(localData?.stash) ? localData.stash : [];
  const localIntake = Array.isArray(localData?.intake) ? localData.intake : [];
  const localSessions = Array.isArray(localData?.sessions) ? localData.sessions : [];
  const localCheckins = Array.isArray(localData?.checkins) ? localData.checkins : [];

  // Stash rows have no stable client-to-server id mapping across local/cloud.
  // Deduplicate only exact item copies, not legitimate re-ups or same-name items.
  if (localStash.length > 0) {
    const cloudSignatures = new Set((cloud.stash || []).map(stashSignature));
    for (const item of localStash) {
      const sig = stashSignature(item);
      if (!item?.name || cloudSignatures.has(sig)) {
        summary.skipped.stash += 1;
        continue;
      }
      await postAwait('/stash', toStashBody(item));
      cloudSignatures.add(sig);
      summary.uploaded.stash += 1;
    }
  }

  const intakeTs = new Set((cloud.intake || []).map((e) => e.ts).filter(Boolean));
  for (const entry of localIntake) {
    if (!entry?.ts || intakeTs.has(entry.ts)) {
      summary.skipped.intake += 1;
      continue;
    }
    await postAwait('/intake', toIntakeBody(entry));
    intakeTs.add(entry.ts);
    summary.uploaded.intake += 1;
  }

  const sessionTs = new Set((cloud.sessions || []).map((e) => e.ts).filter(Boolean));
  for (const entry of localSessions) {
    if (!entry?.ts || sessionTs.has(entry.ts)) {
      summary.skipped.sessions += 1;
      continue;
    }
    await postAwait('/sessions', {
      medicated: entry.used ?? true,
      ts: entry.ts,
    });
    sessionTs.add(entry.ts);
    summary.uploaded.sessions += 1;
  }

  const checkinTs = new Set((cloud.checkins || []).map((e) => e.ts).filter(Boolean));
  for (const entry of localCheckins) {
    if (!entry?.ts || checkinTs.has(entry.ts)) {
      summary.skipped.checkins += 1;
      continue;
    }
    await postAwait('/checkins', {
      feelings:   entry.feelings   || [],
      activities: entry.activities || [],
      notes:      entry.note       || null,
      ts:         entry.ts,
    });
    checkinTs.add(entry.ts);
    summary.uploaded.checkins += 1;
  }

  return summary;
}
